import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { format } from "date-fns";
import { Helmet } from "react-helmet-async";

interface BlogPostData {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  cover_image: string | null;
  author_name: string;
  published_at: string | null;
  created_at: string;
}

const formatBlogContent = (html: string): string => {
  let cleaned = html;

  // Fix pattern: <strong>Title</strong><h1></h1> → <h2>Title</h2>
  cleaned = cleaned.replace(/<strong>([^<]+)<\/strong>\s*<h[1-6]>\s*<\/h[1-6]>/gi, '<h2>$1</h2>');
  
  // Remove standalone empty heading tags
  cleaned = cleaned.replace(/<h[1-6]>\s*<\/h[1-6]>/gi, '');

  // Fix broken list items: content outside <li> tags within lists
  cleaned = cleaned.replace(/<\/li>\s*\n\s*(?!<li|<\/[uo]l)([^<\n]+)/gi, '</li>\n  <li>$1');

  // Now split into lines, wrap bare text in <p>, and promote standalone <strong> lines to <h2>
  const lines = cleaned.split('\n');
  const blockTagRegex = /^\s*<(p|div|h[1-6]|ul|ol|li|blockquote|table|figure|section|article|pre|hr|img|br|\/)[\s>\/]/i;
  const standaloneBoldRegex = /^\s*<strong>([^<]+)<\/strong>\s*$/i;
  
  const result: string[] = [];
  let bareTextBuffer: string[] = [];

  const flushBareText = () => {
    if (bareTextBuffer.length > 0) {
      const text = bareTextBuffer.join(' ').trim();
      if (text) result.push(`<p>${text}</p>`);
      bareTextBuffer = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushBareText();
      continue;
    }
    // Promote standalone <strong>Text</strong> lines to <h2> (section headings)
    const boldMatch = trimmed.match(standaloneBoldRegex);
    if (boldMatch) {
      flushBareText();
      result.push(`<h2>${boldMatch[1]}</h2>`);
      continue;
    }
    if (blockTagRegex.test(trimmed)) {
      flushBareText();
      result.push(trimmed);
    } else {
      bareTextBuffer.push(trimmed);
    }
  }
  flushBareText();

  return result.join('\n');
};

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    const { data } = await supabase
      .from("blog_posts")
      .select("*")
      .eq("slug", slug)
      .eq("status", "published")
      .single();
    setPost(data);
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <Skeleton className="h-10 w-3/4 mb-4" />
        <Skeleton className="h-64 w-full mb-6" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold mb-4">Post not found</h1>
        <Button asChild><Link to="/blog"><ArrowLeft className="h-4 w-4 mr-2" />Back to Blog</Link></Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{`${post.title} | SabiTradeNG Blog`}</title>
        {post.excerpt && <meta name="description" content={post.excerpt.slice(0, 160)} />}
        <link rel="canonical" href={`https://sabitradeng.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        {post.excerpt && <meta property="og:description" content={post.excerpt.slice(0, 160)} />}
        <meta property="og:url" content={`https://sabitradeng.com/blog/${post.slug}`} />
        {post.cover_image && <meta property="og:image" content={post.cover_image} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.excerpt || undefined,
          image: post.cover_image || undefined,
          author: { "@type": "Person", name: post.author_name || "SabiTradeNG" },
          datePublished: post.published_at || post.created_at,
          publisher: { "@type": "Organization", name: "SabiTradeNG" },
          mainEntityOfPage: `https://sabitradeng.com/blog/${post.slug}`,
        })}</script>
      </Helmet>
      <article className="container mx-auto px-4 py-12 max-w-3xl">
        <Link to="/blog" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Blog
        </Link>

        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">{post.title}</h1>

        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8">
          <span className="flex items-center gap-1"><User className="h-4 w-4" />{post.author_name}</span>
          <span className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {format(new Date(post.published_at || post.created_at), "MMMM d, yyyy")}
          </span>
        </div>

        {post.cover_image && (
          <img src={post.cover_image} alt={post.title} className="w-full rounded-xl mb-8 max-h-96 object-cover" />
        )}

        <style dangerouslySetInnerHTML={{ __html: `
          .blog-content h1 { font-size: 2em; font-weight: 800; margin-top: 2em; margin-bottom: 0.75em; line-height: 1.2; }
          .blog-content h2 { font-size: 1.5em; font-weight: 700; margin-top: 2em; margin-bottom: 0.75em; line-height: 1.3; border-bottom: 2px solid hsl(var(--border)); padding-bottom: 0.5em; }
          .blog-content h3 { font-size: 1.25em; font-weight: 600; margin-top: 1.5em; margin-bottom: 0.5em; }
          .blog-content p { margin-bottom: 1.25em; line-height: 1.8; }
          .blog-content ul, .blog-content ol { margin: 1em 0; padding-left: 1.5em; }
          .blog-content li { margin-bottom: 0.5em; line-height: 1.7; }
          .blog-content ul li { list-style-type: disc; }
          .blog-content ol li { list-style-type: decimal; }
          .blog-content blockquote { border-left: 4px solid hsl(var(--primary)); padding: 0.75em 1em; margin: 1.5em 0; font-style: italic; background: hsl(var(--muted)); border-radius: 0 0.5em 0.5em 0; }
          .blog-content a { color: hsl(var(--primary)); text-decoration: underline; }
          .blog-content img { border-radius: 0.75em; margin: 1.5em 0; max-width: 100%; }
          .blog-content code { background: hsl(var(--muted)); padding: 0.15em 0.4em; border-radius: 0.25em; font-size: 0.9em; }
          .blog-content strong { font-weight: 600; color: hsl(var(--foreground)); }
        `}} />

        <div
          className="blog-content text-muted-foreground text-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: formatBlogContent(post.content) }}
        />
      </article>
    </div>
  );
}
